import type { StateCreator } from 'zustand'
import type { ClashProgress, ClashResult, ClashStats } from '@viewer/clash/lib'
import {
  emptySide,
  type ClashGroupBy,
  type ClashSide,
} from '@viewer/clash/clashSets'
import {
  forgetMissing,
  setTriage,
  setTriageMany,
  temDecisao,
  type ClashTriageMap,
  type ClashTriagePatch,
} from '@viewer/clash/clashTriage'
import {
  decididasGuardadas,
  leituraBloqueada,
  loadCorridas,
  loadSetup,
  loadTriage,
  loadClassificacao,
  persistCorridas,
  persistSetup,
  persistTriage,
  persistTriagemEClassificacao,
  persistTudo,
  type ClashSetupFile,
} from '@viewer/clash/clashTriageStorage'
import {
  aplicarAgrupamento,
  aplicarClassificacao,
  classificacaoVazia,
  juntarGrupos,
  renomearGrupo,
  tirarDoGrupo,
  type ClashClassificacaoFile,
  type ItemClassificado,
} from '@viewer/clash/clashAgrupamentoEstado'
import type { GrupoCalculado } from '@viewer/clash/clashAgrupamento'
import type {
  ClashCorrida,
  ClashEnvioModulo,
} from '@viewer/clash/clashReconciliacao'
import type { VnSessionConflitos } from '@viewer/clash/clashSessao'
import { comUltimo } from '@viewer/clash/clashReconciliacao'
import {
  migrarChavesDaExportacao,
  type ClashUltimaExportacao,
} from '@viewer/clash/clashExportacaoRegisto'
import { migrarChavesDoEnvio } from '@viewer/clash/clashModuloBcf'
import { comOrigem } from '@viewer/clash/clashAlteracaoOrigem'
import {
  FOLGA_PARES_POR_OMISSAO,
  lerFolgaPares,
  PENETRACAO_MINIMA_POR_OMISSAO,
  type ExcluidasAEntrada,
} from '@viewer/clash/clashFiltroEntrada'

/** O que uma corrida acabada grava. */
export interface ClashCorridaPorGravar {
  triagem: ClashTriageMap
  corrida: ClashCorrida
  chaves: string[]
  resumo: string
  /** Chaves migradas por GlobalId nesta corrida (antiga para nova): os
   *  topicos enviados para o modulo e o registo da ultima exportacao
   *  passam para as chaves novas. */
  migradas?: Record<string, string>
}

/** A parte das corridas que esta em memoria, para gravar. */
function corridasDe(s: {
  clashHistorico: ClashCorrida[]
  clashProjectoBcf: string | null
  clashChavesUltima: string[]
  clashModulo: ClashEnvioModulo | null
  clashUltimaExportacao: ClashUltimaExportacao | null
}) {
  return {
    historico: s.clashHistorico,
    projectoBcf: s.clashProjectoBcf,
    chavesUltimaCorrida: s.clashChavesUltima,
    modulo: s.clashModulo,
    ultimaExportacao: s.clashUltimaExportacao,
  }
}

/** Abaixo disto a rede de seguranca nao se mete (triagens pequenas). */
const MINIMO_PARA_RETER = 4

function contarDecididas(t: ClashTriageMap): number {
  return Object.values(t).filter(temDecisao).length
}

// ---------------------------------------------------------------------------
// Estado do painel de conflitos.
//
// A divisao e a mesma do IDS e da Tabela (beta.75/76), e nao e cosmetica:
//
//   RESULTADOS  derivados dos modelos + da configuracao -> MEMORIA. Um
//               reload nao os traz de volta; trocar de IFC limpa-os com
//               aviso (`clashStale`), porque descrevem elementos que ja
//               nao estao carregados.
//   TRIAGEM     decisao do utilizador (aceite / resolvido / nota) ->
//               TRABALHO. Vai para o buffer de recuperacao, entra no
//               manifesto e no ficheiro de sessao (`local_work`), e
//               sobrevive a novas corridas atraves do `clashKey`.
//
// A CONFIGURACAO (conjuntos A x B, tolerancia, folga) mudou de lado em
// beta.81 e passou a TRABALHO, junto com a triagem. Antes eram "dois
// selectores que se refazem em segundos"; desde que um lado pode ser uma
// REGRA escrita a mao (o construtor de filtros do Shift+F), a
// configuracao passou a ser o enunciado do teste. Vai no mesmo ficheiro
// da triagem, ver `clashTriageStorage`.
// ---------------------------------------------------------------------------

/** Assinatura dos modelos carregados. Muda quando se abre, fecha ou
 *  troca um IFC, e e isso que invalida os resultados em memoria. Mesmo
 *  padrao do `idsModelKey` / `infoTableModelKey`. */
export function clashModelKey(
  models: { id: string; fileName: string }[],
): string {
  return models.map((m) => `${m.id} ${m.fileName}`).join('\n')
}

/** Valores por omissao do painel, em metros. Tolerancia 1 mm e a do
 *  motor (docs/CLASH.md); folga desligada porque liga-la e a decisao que
 *  mais aumenta o numero de resultados. */
export const CLASH_DEFAULT_TOLERANCE = 0.001
export const CLASH_DEFAULT_CLEARANCE = 0
export const CLASH_DEFAULT_IGNORE_CONTACTS = 0

/** Geometria do painel flutuante do ribbon. Preferencia de quem usa, nao
 *  trabalho: vive em localStorage e nunca entra na recuperacao. */
export interface ClashPanelRect {
  x: number
  y: number
  w: number
  h: number
}

export const CLASH_PANEL_DEFAULT_RECT: ClashPanelRect = {
  x: -1,
  y: 56,
  w: 440,
  h: 620,
}

const PANEL_RECT_KEY = 'vn2r.clashPanelRect'
const PANEL_MAX_KEY = 'vn2r.clashPanelMaximized'

/** Limites do painel flutuante. O minimo nao e cosmetico: a lista com os
 *  botoes de triagem deixa de ser utilizavel abaixo disto. */
export const CLASH_PANEL_MIN_W = 320
export const CLASH_PANEL_MIN_H = 240

export function clampClashPanelRect(
  rect: ClashPanelRect,
  viewport?: { w: number; h: number },
): ClashPanelRect {
  const vw =
    viewport?.w ?? (typeof window === 'undefined' ? 1440 : window.innerWidth)
  const vh =
    viewport?.h ?? (typeof window === 'undefined' ? 900 : window.innerHeight)
  const w = Math.max(CLASH_PANEL_MIN_W, Math.min(Math.round(rect.w), vw))
  const h = Math.max(CLASH_PANEL_MIN_H, Math.min(Math.round(rect.h), vh))
  // x = -1 significa "encostado a direita", que e onde o painel sempre
  // nasceu. Guardar a posicao calculada era prende-lo a largura da janela
  // em que foi aberto pela primeira vez.
  const x =
    rect.x < 0 ? -1 : Math.max(0, Math.min(Math.round(rect.x), vw - w))
  const y = Math.max(0, Math.min(Math.round(rect.y), vh - 40))
  return { x, y, w, h }
}

function loadPanelRect(): ClashPanelRect {
  try {
    const raw = localStorage.getItem(PANEL_RECT_KEY)
    if (!raw) return CLASH_PANEL_DEFAULT_RECT
    const parsed: unknown = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return CLASH_PANEL_DEFAULT_RECT
    const r = parsed as Partial<ClashPanelRect>
    if (
      typeof r.x !== 'number' ||
      typeof r.y !== 'number' ||
      typeof r.w !== 'number' ||
      typeof r.h !== 'number'
    ) {
      return CLASH_PANEL_DEFAULT_RECT
    }
    return clampClashPanelRect(r as ClashPanelRect)
  } catch {
    return CLASH_PANEL_DEFAULT_RECT
  }
}

function loadPanelMaximized(): boolean {
  try {
    return localStorage.getItem(PANEL_MAX_KEY) === '1'
  } catch {
    return false
  }
}

export interface ClashSlice {
  clashPanelOpen: boolean
  setClashPanelOpen: (open: boolean) => void

  // --- Janela do painel flutuante (atalho do ribbon) -------------------
  clashPanelRect: ClashPanelRect
  setClashPanelRect: (rect: ClashPanelRect) => void
  clashPanelMaximized: boolean
  setClashPanelMaximized: (on: boolean) => void
  /** Painel numa janela real do browser (segundo ecra). Nao persiste: um
   *  arranque nunca pode tentar abrir uma popup sozinho. */
  clashPanelDetached: boolean
  setClashPanelDetached: (on: boolean) => void

  // --- Conjuntos A x B e configuracao ---------------------------------
  clashSideA: ClashSide
  clashSideB: ClashSide
  setClashSide: (which: 'a' | 'b', side: ClashSide) => void
  /** Nome do preset activo, so para a UI marcar o botao. */
  clashPreset: string
  setClashPreset: (name: string) => void

  /** Metros. */
  clashTolerance: number
  clashClearance: number
  clashIgnoreContacts: number
  clashSelfClash: boolean
  /** Filtro a entrada (clashFiltroEntrada.ts): penetracao minima, m, e
   *  pares de especialidades em que se verifica a folga. */
  clashPenetracaoMinima: number
  clashFolgaPares: string[]
  setClashTolerance: (m: number) => void
  setClashClearance: (m: number) => void
  setClashIgnoreContacts: (m: number) => void
  setClashSelfClash: (on: boolean) => void
  setClashPenetracaoMinima: (m: number) => void
  setClashFolgaPares: (pares: string[]) => void

  // --- Corrida ---------------------------------------------------------
  clashRunning: boolean
  clashProgress: ClashProgress | null
  clashResults: ClashResult[]
  clashStats: ClashStats | null
  clashError: string | null
  /** Momento em que a ultima corrida terminou (ms). 0 = nunca correu. */
  clashRanAt: number
  startClashRun: () => void
  setClashProgress: (p: ClashProgress) => void
  finishClashRun: (
    results: ClashResult[],
    stats: ClashStats,
    key: string,
    now?: number,
  ) => void
  failClashRun: (msg: string) => void
  /** O que o filtro a entrada deixou de fora na ultima corrida. */
  clashExcluidasEntrada: ExcluidasAEntrada | null
  setClashExcluidasEntrada: (x: ExcluidasAEntrada | null) => void

  // --- Lista -----------------------------------------------------------
  clashGroupBy: ClashGroupBy
  setClashGroupBy: (g: ClashGroupBy) => void
  /** Conflito em foco (o ultimo em que se clicou). */
  clashFocusKey: string | null
  setClashFocusKey: (key: string | null) => void
  /** Conflitos marcados para exportar. */
  clashChecked: Set<string>
  toggleClashChecked: (key: string) => void
  setClashChecked: (keys: string[]) => void
  /** O 3D esta isolado nos elementos de um conflito. */
  clashIsolated: boolean
  setClashIsolated: (on: boolean) => void

  // --- Invalidacao por troca de modelos --------------------------------
  clashModelKey: string
  clashStale: boolean
  syncClashModels: (key: string) => void
  dismissClashStale: () => void

  // --- Triagem (TRABALHO) ----------------------------------------------
  clashTriage: ClashTriageMap
  clashTriageHydrated: boolean
  hydrateClashTriage: () => void
  applyClashTriage: (key: string, patch: ClashTriagePatch) => void
  /** O mesmo patch (ou um por chave) em vários conflitos, gravado uma
   *  vez: edição em lote e importação de proposta. */
  applyClashTriageMany: (
    keys: readonly string[],
    patch: ClashTriagePatch | ((key: string) => ClashTriagePatch),
  ) => void
  forgetClashTriage: (keys: string[]) => void

  // --- Configuracao do teste (TRABALHO) ---------------------------------
  clashSetupHydrated: boolean
  /** Le a configuracao gravada. Como a triagem: so depois de o arranque
   *  decidir se recupera ou descarta. */
  hydrateClashSetup: () => void
  /** Grava a configuracao actual. Chamado por quem a muda. */
  persistClashSetup: () => void

  // --- Corridas (TRABALHO, no mesmo ficheiro da triagem) ---------------
  /** Historico das corridas (data, modelos, configuracao, contagens). */
  clashHistorico: ClashCorrida[]
  /** Identificador do projecto: da Guids BCF estaveis entre exportacoes. */
  clashProjectoBcf: string | null
  /** Chaves da ultima corrida, para contar os novos na seguinte. */
  clashChavesUltima: string[]
  /** Topicos enviados para o modulo BCF. */
  clashModulo: ClashEnvioModulo | null
  /** Registo da ultima exportacao para ficheiro (o que saiu e com que
   *  estado), para "So o que mudou desde a ultima exportacao". */
  clashUltimaExportacao: ClashUltimaExportacao | null
  setClashUltimaExportacao: (r: ClashUltimaExportacao | null) => void
  /** Frase do resumo da ultima reconciliacao (so em memoria). */
  clashResumoCorrida: string | null
  /** Grava o resultado de uma corrida: a triagem reconciliada, a
   *  corrida no historico e as chaves. */
  registarCorrida: (o: ClashCorridaPorGravar) => boolean
  /** Reconciliacao retida pela rede de seguranca: ia deixar a triagem
   *  com menos de metade das entradas com decisao. Nada foi gravado. */
  clashReconciliacaoRetida: (ClashCorridaPorGravar & { antes: number; depois: number }) | null
  /** O utilizador confirmou: grava a reconciliacao retida. */
  confirmarReconciliacaoRetida: () => void
  /** O utilizador recusou: fica a triagem guardada, a corrida nao entra
   *  no historico. */
  descartarReconciliacaoRetida: () => void
  /** Grava sem a rede de seguranca (uso interno). */
  gravarCorrida: (o: ClashCorridaPorGravar) => void
  /** O identificador do projecto, criado da primeira vez. */
  garantirProjectoBcf: () => string
  setClashModulo: (m: ClashEnvioModulo | null) => void
  setClashResumoCorrida: (t: string | null) => void
  /** Repoe a deteccao de conflitos de uma sessao (a decisao de
   *  substituir a triagem do browser e de quem chama). */
  restaurarConflitos: (s: VnSessionConflitos) => void

  // --- Classificacao e agrupamento (TRABALHO, no ficheiro da triagem) ---
  /** Regras, especialidades corrigidas, metodo de agrupamento e grupos. */
  clashClassificacao: ClashClassificacaoFile
  /** Muda as regras, as especialidades ou o metodo (e grava). */
  setClashClassificacao: (patch: Partial<Omit<ClashClassificacaoFile, 'grupos'>>) => void
  /** Escreve o parecer das regras nos conflitos sem parecer manual. */
  classificarClashTriagem: (itens: readonly ItemClassificado[]) => {
    aplicadas: number
    mantidas: number
    foraDoAmbito: number
  }
  /** Poe os conflitos presentes nos grupos calculados. */
  agruparClashTriagem: (grupos: readonly GrupoCalculado[], presentes: ReadonlySet<string>) => void
  /** Edicoes a mao dos grupos (ficam fixas ao voltar a agrupar). */
  juntarClashGrupos: (ids: readonly string[]) => void
  tirarDoClashGrupo: (keys: readonly string[]) => void
  renomearClashGrupo: (id: string, titulo: string) => void

  /** Lousa limpa. Chamado pelo "Fechar sessao". */
  resetClash: () => void
}

/** Tudo o que e derivado dos modelos. Uma troca de IFCs leva isto. */
function derivedInitialState() {
  return {
    clashRunning: false,
    clashProgress: null as ClashProgress | null,
    clashResults: [] as ClashResult[],
    clashStats: null as ClashStats | null,
    clashError: null as string | null,
    clashRanAt: 0,
    clashFocusKey: null as string | null,
    clashChecked: new Set<string>(),
    clashIsolated: false,
    clashExcluidasEntrada: null as ExcluidasAEntrada | null,
  }
}

/** O que sobrevive a troca de IFCs num lado. As categorias e as regras
 *  ficam: falam a linguagem do modelo, e o modelId e o nome do ficheiro,
 *  por isso uma revisao nova do mesmo ficheiro continua a casar. Sem
 *  isto, repetir a corrida depois de abrir as revisoes dava sempre
 *  "conjuntos diferentes" e a reconciliacao nunca era comparavel. So a
 *  SELECCAO sai: aponta para localIds do modelo que ja nao esta. */
function keepAcrossModels(side: ClashSide): ClashSide {
  if (side.source === 'selection') return emptySide()
  return side
}

/** Fotografia da configuracao para gravar. */
function setupOf(s: ClashSlice): ClashSetupFile {
  return {
    sideA: s.clashSideA,
    sideB: s.clashSideB,
    preset: s.clashPreset,
    tolerance: s.clashTolerance,
    clearance: s.clashClearance,
    ignoreContacts: s.clashIgnoreContacts,
    selfClash: s.clashSelfClash,
    penetracaoMinima: s.clashPenetracaoMinima,
    folgaPares: [...s.clashFolgaPares],
  }
}

/** A configuracao de um ficheiro, como campos do store. Um so sitio
 *  para quem repoe (hidratar, sessao, partilha). */
export function camposDoSetup(setup: ClashSetupFile): Pick<
  ClashSlice,
  | 'clashSideA'
  | 'clashSideB'
  | 'clashPreset'
  | 'clashTolerance'
  | 'clashClearance'
  | 'clashIgnoreContacts'
  | 'clashSelfClash'
  | 'clashPenetracaoMinima'
  | 'clashFolgaPares'
> {
  return {
    clashSideA: setup.sideA,
    clashSideB: setup.sideB,
    clashPreset: setup.preset,
    clashTolerance: setup.tolerance,
    clashClearance: setup.clearance,
    clashIgnoreContacts: setup.ignoreContacts,
    clashSelfClash: setup.selfClash,
    clashPenetracaoMinima: setup.penetracaoMinima,
    clashFolgaPares: [...setup.folgaPares],
  }
}

/** A configuracao actual, para gravar ou partilhar. */
export function setupDoEstado(s: Pick<
  ClashSlice,
  | 'clashSideA'
  | 'clashSideB'
  | 'clashPreset'
  | 'clashTolerance'
  | 'clashClearance'
  | 'clashIgnoreContacts'
  | 'clashSelfClash'
  | 'clashPenetracaoMinima'
  | 'clashFolgaPares'
>): ClashSetupFile {
  return setupOf(s as ClashSlice)
}

export const createClashSlice: StateCreator<ClashSlice> = (set, get) => {
  /** Grava a configuracao a seguir a uma mudanca. So depois de ela ter
   *  sido lida: gravar antes de hidratar escrevia por cima do trabalho
   *  guardado com os valores por omissao. */
  const saveSetup = () => {
    const s = get()
    if (!s.clashSetupHydrated) return
    persistSetup(setupOf(s))
  }
  return {
  clashPanelOpen: false,
  setClashPanelOpen: (open) => set({ clashPanelOpen: open }),

  clashPanelRect: loadPanelRect(),
  setClashPanelRect: (rect) => {
    const clamped = clampClashPanelRect(rect)
    try {
      localStorage.setItem(PANEL_RECT_KEY, JSON.stringify(clamped))
    } catch {
      /* ignora */
    }
    set({ clashPanelRect: clamped })
  },
  clashPanelMaximized: loadPanelMaximized(),
  setClashPanelMaximized: (on) => {
    try {
      localStorage.setItem(PANEL_MAX_KEY, on ? '1' : '0')
    } catch {
      /* ignora */
    }
    set({ clashPanelMaximized: on })
  },
  clashPanelDetached: false,
  setClashPanelDetached: (on) => set({ clashPanelDetached: on }),

  clashSideA: emptySide(),
  clashSideB: emptySide(),
  setClashSide: (which, side) => {
    set(which === 'a' ? { clashSideA: side } : { clashSideB: side })
    saveSetup()
  },
  clashPreset: '',
  setClashPreset: (name) => {
    set({ clashPreset: name })
    saveSetup()
  },

  clashTolerance: CLASH_DEFAULT_TOLERANCE,
  clashClearance: CLASH_DEFAULT_CLEARANCE,
  clashIgnoreContacts: CLASH_DEFAULT_IGNORE_CONTACTS,
  clashSelfClash: false,
  setClashTolerance: (m) => {
    set({ clashTolerance: Number.isFinite(m) && m > 0 ? m : 0 })
    saveSetup()
  },
  setClashClearance: (m) => {
    set({ clashClearance: Number.isFinite(m) && m > 0 ? m : 0 })
    saveSetup()
  },
  setClashIgnoreContacts: (m) => {
    set({ clashIgnoreContacts: Number.isFinite(m) && m > 0 ? m : 0 })
    saveSetup()
  },
  setClashSelfClash: (on) => {
    set({ clashSelfClash: on })
    saveSetup()
  },
  clashPenetracaoMinima: PENETRACAO_MINIMA_POR_OMISSAO,
  clashFolgaPares: [...FOLGA_PARES_POR_OMISSAO],
  setClashPenetracaoMinima: (m) => {
    set({ clashPenetracaoMinima: Number.isFinite(m) && m > 0 ? m : 0 })
    saveSetup()
  },
  setClashFolgaPares: (pares) => {
    set({ clashFolgaPares: lerFolgaPares(pares) })
    saveSetup()
  },

  ...derivedInitialState(),

  startClashRun: () =>
    set({
      clashRunning: true,
      clashError: null,
      clashProgress: null,
      clashResults: [],
      clashStats: null,
      clashFocusKey: null,
      clashChecked: new Set<string>(),
      clashExcluidasEntrada: null,
    }),
  setClashProgress: (p) => set({ clashProgress: p }),
  // Resultados novos por cima: o aviso de invalidacao ja nao tem assunto.
  finishClashRun: (results, stats, key, now = Date.now()) =>
    set({
      clashResults: results,
      clashStats: stats,
      clashRunning: false,
      clashProgress: null,
      clashModelKey: key,
      clashStale: false,
      clashRanAt: now,
    }),
  failClashRun: (msg) =>
    set({ clashError: msg, clashRunning: false, clashProgress: null }),
  setClashExcluidasEntrada: (x) => set({ clashExcluidasEntrada: x }),

  clashGroupBy: 'severity',
  setClashGroupBy: (g) => set({ clashGroupBy: g }),
  setClashFocusKey: (key) => set({ clashFocusKey: key }),
  toggleClashChecked: (key) =>
    set((s) => {
      const next = new Set(s.clashChecked)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return { clashChecked: next }
    }),
  setClashChecked: (keys) => set({ clashChecked: new Set(keys) }),
  setClashIsolated: (on) => set({ clashIsolated: on }),

  clashModelKey: '',
  clashStale: false,
  syncClashModels: (key) =>
    set((s) => ({
      ...derivedInitialState(),
      clashModelKey: key,
      // So se avisa quem perdeu alguma coisa. O aviso e pegajoso porque
      // trocar de IFC passa por descarregar o velho e carregar o novo:
      // sao duas trocas seguidas e so a primeira ainda ve os resultados.
      clashStale: s.clashStale || s.clashResults.length > 0,
      // Os lados ficam (categorias, modelos por nome e regras): deita-los
      // fora ao abrir uma revisao era apagar o enunciado do teste e
      // impedir a corrida comparavel. So a seleccao sai (keepAcrossModels).
      clashSideA: keepAcrossModels(s.clashSideA),
      clashSideB: keepAcrossModels(s.clashSideB),
    })),
  dismissClashStale: () => set({ clashStale: false }),

  clashTriage: {},
  clashTriageHydrated: false,
  hydrateClashTriage: () => {
    if (get().clashTriageHydrated) return
    // Com a recuperacao por decidir as leituras dao vazio: hidratar agora
    // marcava como lido um mapa vazio, e a gravacao seguinte apagava a
    // triagem guardada. Fica por hidratar; quem precisa espera pela
    // decisao (whenRecoveryResolved) e volta a chamar.
    if (leituraBloqueada()) return
    const c = loadCorridas()
    const classificacao = loadClassificacao()
    // O que ja estiver em memoria (feito antes de hidratar) junta-se ao
    // guardado, por cima dele, e grava-se: nada se perde.
    const emMemoria = get().clashTriage
    const triagem = { ...loadTriage(), ...emMemoria }
    if (Object.keys(emMemoria).length > 0) persistTriage(triagem)
    // Substituicao: ler o guardado nao e decisao de ninguem (ver
    // clashAlteracaoOrigem.ts).
    comOrigem('substituicao', () =>
      set({
        clashTriage: triagem,
        clashTriageHydrated: true,
        clashHistorico: c.historico,
        clashProjectoBcf: c.projectoBcf,
        clashChavesUltima: c.chavesUltimaCorrida,
        clashModulo: c.modulo,
        clashUltimaExportacao: c.ultimaExportacao,
        clashClassificacao: classificacao,
      }),
    )
  },
  applyClashTriage: (key, patch) => {
    // Nunca gravar por cima da triagem guardada sem a ter lido.
    get().hydrateClashTriage()
    set((s) => {
      const next = comUltimo(setTriage(s.clashTriage, key, patch), [key], s.clashResults)
      persistTriage(next)
      return { clashTriage: next }
    })
  },
  applyClashTriageMany: (keys, patch) => {
    // Nunca gravar por cima da triagem guardada sem a ter lido.
    get().hydrateClashTriage()
    set((s) => {
      if (keys.length === 0) return {}
      const next = comUltimo(setTriageMany(s.clashTriage, keys, patch), keys, s.clashResults)
      persistTriage(next)
      return { clashTriage: next }
    })
  },
  forgetClashTriage: (keys) => {
    // Nunca gravar por cima da triagem guardada sem a ter lido.
    get().hydrateClashTriage()
    set((s) => {
      const next = forgetMissing(s.clashTriage, keys)
      persistTriage(next)
      return { clashTriage: next }
    })
  },

  clashSetupHydrated: false,
  hydrateClashSetup: () => {
    if (get().clashSetupHydrated) return
    if (leituraBloqueada()) return
    const saved = loadSetup()
    comOrigem('substituicao', () =>
      set(
        saved
          ? { clashSetupHydrated: true, ...camposDoSetup(saved) }
          : { clashSetupHydrated: true },
      ),
    )
  },
  persistClashSetup: () => {
    persistSetup(setupOf(get()))
  },

  clashHistorico: [],
  clashProjectoBcf: null,
  clashChavesUltima: [],
  clashModulo: null,
  clashUltimaExportacao: null,
  clashResumoCorrida: null,
  clashReconciliacaoRetida: null,
  registarCorrida: (o) => {
    const s = get()
    // Rede de seguranca: a reconciliacao nunca apaga entradas, so muda
    // estados. Se o mapa a gravar tem menos de metade das entradas com
    // decisao do que o guardado (ou do que estava em memoria), algo leu
    // mal; nao se grava sem o utilizador confirmar.
    const antes = Math.max(decididasGuardadas(), contarDecididas(s.clashTriage))
    const depois = contarDecididas(o.triagem)
    if (antes >= MINIMO_PARA_RETER && depois < antes / 2) {
      set({ clashReconciliacaoRetida: { ...o, antes, depois } })
      return false
    }
    get().gravarCorrida(o)
    return true
  },
  confirmarReconciliacaoRetida: () => {
    const r = get().clashReconciliacaoRetida
    if (!r) return
    set({ clashReconciliacaoRetida: null })
    get().gravarCorrida(r)
  },
  descartarReconciliacaoRetida: () => {
    if (!get().clashReconciliacaoRetida) return
    const c = loadCorridas()
    comOrigem('substituicao', () =>
      set({
        clashReconciliacaoRetida: null,
        clashClassificacao: loadClassificacao(),
        clashTriage: loadTriage(),
        clashHistorico: c.historico,
        clashProjectoBcf: c.projectoBcf,
        clashChavesUltima: c.chavesUltimaCorrida,
        clashModulo: c.modulo,
        clashUltimaExportacao: c.ultimaExportacao,
        clashResumoCorrida: null,
      }),
    )
  },
  gravarCorrida: ({ triagem, corrida: corrida0, chaves, resumo, migradas }) => {
    const s = get()
    // O metodo de agrupamento e as regras em vigor ficam com a corrida.
    const cl = s.clashClassificacao
    const corrida: ClashCorrida =
      cl.agrupadoEm || cl.classificadoEm
        ? {
            ...corrida0,
            agrupamento: {
              metodo: cl.agrupamento.metodo,
              parametros: { ...cl.agrupamento } as unknown as Record<string, unknown>,
              regras: { ...cl.regras } as unknown as Record<string, unknown>,
              grupos: Object.keys(cl.grupos).length,
            },
          }
        : corrida0
    const historico = [...s.clashHistorico, corrida].slice(-50)
    // Um conflito migrado por GlobalId continua a ser o mesmo: o topico
    // enviado e o registo da exportacao seguem-no para a chave nova.
    const modulo = migradas ? migrarChavesDoEnvio(s.clashModulo, migradas) : s.clashModulo
    const ultimaExportacao = migradas
      ? migrarChavesDaExportacao(s.clashUltimaExportacao, migradas)
      : s.clashUltimaExportacao
    persistTudo(triagem, s.clashSetupHydrated ? setupOf(s) : loadSetup(), {
      historico,
      projectoBcf: s.clashProjectoBcf,
      chavesUltimaCorrida: chaves,
      modulo,
      ultimaExportacao,
    })
    // Reconciliacao: muda estados, nunca apaga (ver
    // clashAlteracaoOrigem.ts).
    comOrigem('reconciliacao', () =>
      set({
        clashTriage: triagem,
        clashHistorico: historico,
        clashChavesUltima: chaves,
        clashModulo: modulo,
        clashUltimaExportacao: ultimaExportacao,
        clashResumoCorrida: resumo,
      }),
    )
  },
  garantirProjectoBcf: () => {
    const s = get()
    if (s.clashProjectoBcf) return s.clashProjectoBcf
    const id = crypto.randomUUID()
    persistCorridas({ ...corridasDe(s), projectoBcf: id })
    set({ clashProjectoBcf: id })
    return id
  },
  setClashModulo: (m) => {
    const s = get()
    persistCorridas({ ...corridasDe(s), modulo: m })
    set({ clashModulo: m })
  },
  setClashUltimaExportacao: (r) => {
    // Nunca gravar por cima das corridas guardadas sem as ter lido.
    get().hydrateClashTriage()
    const s = get()
    persistCorridas({ ...corridasDe(s), ultimaExportacao: r })
    set({ clashUltimaExportacao: r })
  },
  setClashResumoCorrida: (t) => set({ clashResumoCorrida: t }),
  restaurarConflitos: (sec) => {
    persistTudo(
      sec.triagem,
      sec.setup,
      {
        historico: sec.historico,
        projectoBcf: sec.projectoBcf,
        chavesUltimaCorrida: sec.chavesUltimaCorrida,
        modulo: sec.modulo,
        ultimaExportacao: sec.ultimaExportacao ?? null,
      },
      undefined,
      sec.classificacao ?? undefined,
    )
    comOrigem('substituicao', () => set({
      ...(sec.classificacao ? { clashClassificacao: sec.classificacao } : {}),
      clashTriage: sec.triagem,
      clashTriageHydrated: true,
      clashSetupHydrated: true,
      clashHistorico: sec.historico,
      clashProjectoBcf: sec.projectoBcf,
      clashChavesUltima: sec.chavesUltimaCorrida,
      clashModulo: sec.modulo,
      clashUltimaExportacao: sec.ultimaExportacao ?? null,
      clashResumoCorrida: null,
      ...(sec.setup ? camposDoSetup(sec.setup) : {}),
    }))
  },

  clashClassificacao: classificacaoVazia(),
  setClashClassificacao: (patch) => {
    get().hydrateClashTriage()
    const s = get()
    const next = { ...s.clashClassificacao, ...patch }
    persistTriagemEClassificacao(s.clashTriage, next)
    set({ clashClassificacao: next })
  },
  classificarClashTriagem: (itens) => {
    get().hydrateClashTriage()
    const s = get()
    const agora = new Date().toISOString()
    const r = aplicarClassificacao(s.clashTriage, itens, agora)
    const triagem = comUltimo(r.triagem, itens.map((i) => i.key), s.clashResults)
    const classificacao = { ...s.clashClassificacao, classificadoEm: agora }
    persistTriagemEClassificacao(triagem, classificacao)
    set({ clashTriage: triagem, clashClassificacao: classificacao })
    return { aplicadas: r.aplicadas, mantidas: r.mantidas, foraDoAmbito: r.foraDoAmbito }
  },
  agruparClashTriagem: (grupos, presentes) => {
    get().hydrateClashTriage()
    const s = get()
    const agora = new Date().toISOString()
    const r = aplicarAgrupamento(s.clashTriage, grupos, s.clashClassificacao.grupos, presentes, agora)
    const triagem = comUltimo(r.triagem, [...presentes], s.clashResults)
    const classificacao = { ...s.clashClassificacao, grupos: r.grupos, agrupadoEm: agora }
    persistTriagemEClassificacao(triagem, classificacao)
    set({ clashTriage: triagem, clashClassificacao: classificacao })
  },
  juntarClashGrupos: (ids) => {
    get().hydrateClashTriage()
    const s = get()
    const r = juntarGrupos(s.clashTriage, s.clashClassificacao.grupos, ids)
    const classificacao = { ...s.clashClassificacao, grupos: r.grupos }
    persistTriagemEClassificacao(r.triagem, classificacao)
    set({ clashTriage: r.triagem, clashClassificacao: classificacao })
  },
  tirarDoClashGrupo: (keys) => {
    get().hydrateClashTriage()
    const s = get()
    const triagem = tirarDoGrupo(s.clashTriage, keys)
    persistTriagemEClassificacao(triagem, s.clashClassificacao)
    set({ clashTriage: triagem })
  },
  renomearClashGrupo: (id, titulo) => {
    get().hydrateClashTriage()
    const s = get()
    const r = renomearGrupo(s.clashTriage, s.clashClassificacao.grupos, id, titulo)
    const classificacao = { ...s.clashClassificacao, grupos: r.grupos }
    persistTriagemEClassificacao(r.triagem, classificacao)
    set({ clashTriage: r.triagem, clashClassificacao: classificacao })
  },

  resetClash: () =>
    comOrigem('substituicao', () => set({
      ...derivedInitialState(),
      clashPanelOpen: false,
      clashSideA: emptySide(),
      clashSideB: emptySide(),
      clashPreset: '',
      clashTolerance: CLASH_DEFAULT_TOLERANCE,
      clashClearance: CLASH_DEFAULT_CLEARANCE,
      clashIgnoreContacts: CLASH_DEFAULT_IGNORE_CONTACTS,
      clashSelfClash: false,
      clashPenetracaoMinima: PENETRACAO_MINIMA_POR_OMISSAO,
      clashFolgaPares: [...FOLGA_PARES_POR_OMISSAO],
      clashGroupBy: 'severity',
      clashModelKey: '',
      clashStale: false,
      // A triagem e trabalho, mas "Fechar sessao" e o gesto de lousa
      // limpa: o `resetRecovery` ja apaga a chave de localStorage, por
      // isso deixa-la em memoria era mostrar pareceres sem ficheiro por
      // baixo. Volta a hidratar-se do que la estiver na proxima sessao.
      clashTriage: {},
      clashTriageHydrated: false,
      // A configuracao vive no mesmo ficheiro e segue a mesma regra.
      clashSetupHydrated: false,
      clashPanelDetached: false,
      clashHistorico: [],
      clashProjectoBcf: null,
      clashChavesUltima: [],
      clashModulo: null,
      clashUltimaExportacao: null,
      clashResumoCorrida: null,
      clashReconciliacaoRetida: null,
      clashClassificacao: classificacaoVazia(),
    })),
  }
}
